import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../../../config/config";
import ProjectsSidebar from "./ProjectsSidebar";
import ProjectsCard from "./ProjectsCard";
import { Pagination } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../CompanentMaps/style.css";

const CompanentMaps = () => {
  const [activeSection, setActiveSection] = useState("projects");
  const [directions, setDirections] = useState([]);
  const [investorCategories, setInvestorCategories] = useState([]);
  const [selectedDirectionId, setSelectedDirectionId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [currentItems, setCurrentItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [directionCounts, setDirectionCounts] = useState({});
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalProjectsCount, setTotalProjectsCount] = useState(0);
  const [totalInvestorCount, setTotalInvestorCount] = useState(0);
  const [projectsEntitiesCount, setProjectsEntitiesCount] = useState(0);
  const { t, i18n } = useTranslation();

  const languageMap = { tj: 1, ru: 2, en: 3 };
  const getLanguageCode = (lang) => languageMap[lang] || 2;
  const authHeaders = { accept: "text/plain", "api-version": "1.0" };

  useEffect(() => {
    const langCode = getLanguageCode(i18n.language);

    axios
      .get(`${API_BASE_URL}/api/directions?lang=${langCode}&page=1`, {
        headers: authHeaders,
      })
      .then((res) => {
        const directionsData = res.data.enttities || res.data || [];
        setDirections(directionsData);
        axios
          .get(
            `${API_BASE_URL}/api/events?lang=${langCode}&eventType=2&page=1`,
            {
              headers: authHeaders,
            }
          )
          .then((totalRes) => {
            const totalData = totalRes.data.enttities || totalRes.data || [];
            setTotalProjectsCount(totalData.length);
          })
          .catch((err) => console.error("Error fetching total projects:", err));

        const countPromises = directionsData.map((direction) =>
          axios
            .get(
              `${API_BASE_URL}/api/events/get-project-by-direction-id/${direction.id}?lang=${langCode}`,
              { headers: authHeaders }
            )
            .then((countRes) => ({
              id: direction.id,
              count: (countRes.data.enttities || countRes.data || []).length,
            }))
            .catch(() => ({ id: direction.id, count: 0 }))
        );

        Promise.all(countPromises).then((counts) => {
          const countsMap = {};
          counts.forEach(({ id, count }) => (countsMap[id] = count));
          setDirectionCounts(countsMap);
        });
      })
      .catch((err) => console.error("Error fetching directions:", err));
  }, [i18n.language]);

  useEffect(() => {
    const langCode = getLanguageCode(i18n.language);

    axios
      .get(
        `${API_BASE_URL}/api/investor-guide-category/get-investor-guide-categories?lang=${langCode}&page=1`,
        { headers: authHeaders }
      )
      .then((res) => {
        const categoriesData = res.data.enttities || res.data || [];
        setInvestorCategories(categoriesData);

        axios
          .get(
            `${API_BASE_URL}/api/events?lang=${langCode}&eventType=7&page=1`,
            {
              headers: authHeaders,
            }
          )
          .then((totalRes) => {
            const totalData = totalRes.data.enttities || totalRes.data || [];
            setTotalInvestorCount(totalData.length);
          })
          .catch((err) =>
            console.error("Error fetching total investor guides:", err)
          );

        const countPromises = categoriesData.map((category) =>
          axios
            .get(
              `${API_BASE_URL}/api/events/get-investor-guide-projects-by-category/${category.id}?lang=${langCode}`,
              { headers: authHeaders }
            )
            .then((countRes) => ({
              id: category.id,
              count: (countRes.data.enttities || countRes.data || []).length,
            }))
            .catch(() => ({ id: category.id, count: 0 }))
        );

        Promise.all(countPromises).then((counts) => {
          const countsMap = {};
          counts.forEach(({ id, count }) => (countsMap[id] = count));
          setCategoryCounts(countsMap);
        });
      })
      .catch((err) =>
        console.error("Error fetching investor categories:", err)
      );
  }, [i18n.language]);

  const fetchItems = (page = 1) => {
    const langCode = getLanguageCode(i18n.language);
    setLoading(true);

    let apiUrl = "";

    if (activeSection === "projects") {
      if (selectedDirectionId === null) {
        apiUrl = `${API_BASE_URL}/api/events?lang=${langCode}&eventType=2&page=${page}`;
      } else {
        apiUrl = `${API_BASE_URL}/api/events/get-project-by-direction-id/${selectedDirectionId}?lang=${langCode}`;
      }
    } else {
      if (selectedCategoryId === null) {
        apiUrl = `${API_BASE_URL}/api/events?lang=${langCode}&eventType=7&page=${page}`;
      } else {
        apiUrl = `${API_BASE_URL}/api/events/get-investor-guide-projects-by-category/${selectedCategoryId}?lang=${langCode}`;
      }
    }

    axios
      .get(apiUrl, { headers: authHeaders })
      .then((res) => {
        const entities =
          res.data.entities ||
          res.data.enttities ||
          (Array.isArray(res.data) ? res.data : []);
        setCurrentItems(Array.isArray(entities) ? entities : []);

        if (
          (activeSection === "projects" && selectedDirectionId === null) ||
          (activeSection === "investor" && selectedCategoryId === null)
        ) {
          setTotalPages(Number(res.data.countPage) || 1);
        } else {
          setTotalPages(1);
        }

        if (activeSection === "projects" && selectedDirectionId === null) {
          setProjectsEntitiesCount(res.data.enttitiesCount || entities.length);
        }
      })

      .catch((err) => console.error("Error fetching items:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchItems(1);
  }, [activeSection, selectedDirectionId, selectedCategoryId, i18n.language]);

  useEffect(() => {
    fetchItems(currentPage);
  }, [currentPage]);

  const handlePageChange = (page) => {
    const newPage = Math.max(1, Math.min(page, totalPages));
    setCurrentPage(newPage);
  };

  return (
    <div className="container w-full mx-auto text-white sm:px-4 border-0 shadow-sm rounded-3">
      <div className="card border-0 rounded-3 main-container">
        <div className="card-body p-4">
          <div className="row g-4">
            <div className="col-lg-3 col-md-4">
              <ProjectsSidebar
                activeSection={activeSection}
                setActiveSection={setActiveSection}
                directions={directions}
                investorCategories={investorCategories}
                selectedDirectionId={selectedDirectionId}
                setSelectedDirectionId={setSelectedDirectionId}
                selectedCategoryId={selectedCategoryId}
                setSelectedCategoryId={setSelectedCategoryId}
                directionCounts={directionCounts}
                categoryCounts={categoryCounts}
                totalProjectsCount={projectsEntitiesCount}
                totalInvestorCount={totalInvestorCount}
              />
            </div>

            <div className="col-lg-9 col-md-8">
              <div className="border-bottom border-2 pb-3 mb-4">
                <h2 className="fw-bold text-dark mb-1">
                  <i
                    className={`bi ${
                      activeSection === "projects"
                        ? "bi-folder2-open"
                        : "bi-graph-up-arrow"
                    } me-2 text-primary`}
                  ></i>
                  {activeSection === "projects"
                    ? t("Projects")
                    : t("InvestGuide")}
                </h2>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div
                    className="spinner-border text-primary mb-3"
                    style={{ width: "3rem", height: "3rem" }}
                  ></div>
                  <p className="text-muted">{t("Loading...")}</p>
                </div>
              ) : (
                <>
                  <ProjectsCard
                    items={currentItems}
                    activeSection={activeSection}
                  />

                  {totalPages > 1 && (
                    <Pagination className="justify-content-end mt-4 custom-pagination">
                      <Pagination.First onClick={() => handlePageChange(1)} />
                      <Pagination.Prev
                        onClick={() => handlePageChange(currentPage - 1)}
                      />

                      {(() => {
                        const maxVisible = 4;
                        let startPage = Math.min(
                          Math.max(currentPage - 1, 1),
                          totalPages - maxVisible + 1
                        );
                        let endPage = Math.min(
                          startPage + maxVisible - 1,
                          totalPages
                        );

                        const pages = [];
                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <Pagination.Item
                              key={i}
                              active={i === currentPage}
                              onClick={() => handlePageChange(i)}
                              style={{
                                backgroundColor:
                                  i === currentPage ? "#2B7972" : "transparent",
                                borderColor: "#2B7972",
                                color: i === currentPage ? "#fff" : "#2B7972",
                              }}
                            >
                              {i}
                            </Pagination.Item>
                          );
                        }
                        return pages;
                      })()}

                      <Pagination.Next
                        onClick={() => handlePageChange(currentPage + 1)}
                      />
                      <Pagination.Last
                        onClick={() => handlePageChange(totalPages)}
                      />
                    </Pagination>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanentMaps;
